import type { ProductConfigInput } from "./schema.ts"
import { validateProductConfig } from "./validate.ts"

/**
 * THE only hand-edited place for product numbers. Changing a value is a three-file PR: this file,
 * the golden test (`product-config.test.ts`) and, from T1341, the generated catalogue doc.
 *
 * Amounts are integer cents in AUD, the total the customer pays. We are not registered for GST
 * (T622), so no tax is added and none is mentioned. Every value names the Owner decision that
 * set it; history lives in docs/decisions.md and is never edited when a value changes.
 */
const RAW = {
  currency: "AUD",
  // D-PRICE-KEEP: prices confirmed final, Owner 2026-11-02 (chat). Purchase stays closed by LAUNCH_STAGE.
  pricesDraft: false,
  plans: {
    free: {
      monthlyCents: 0,
      // Owner 2026-10-27 (decisions.md entitlement table, D-TEAMS point 2).
      tripsMonthly: 20,
      // Owner 2026-11-02: 10 -> 20.
      aiReadsMonthly: 20,
      // Owner 2026-10-27 (T412 invoicing quota, entitlement table).
      invoicesMonthly: 5,
      // PRD Q-A2 language assistant quota.
      assistantTurnsMonthly: 30,
      vehicles: 2,
      features: ["logbook", "receipts", "invoicing"],
    },
    logbook: {
      // D-PRICE-KEEP: A$9.99 / month, A$99 / year (Owner 2026-10-27, restated 2026-11-02).
      monthlyCents: 999,
      yearlyCents: 9900,
      tripsMonthly: null,
      aiReadsMonthly: 0,
      invoicesMonthly: 0,
      assistantTurnsMonthly: 300,
      vehicles: 2,
      features: ["logbook"],
    },
    pro: {
      // D-PRICE-KEEP: A$24.99 / month, A$249 / year (Owner 2026-10-27, restated 2026-11-02).
      monthlyCents: 2499,
      yearlyCents: 24900,
      tripsMonthly: null,
      // Owner 2026-11-02 entitlement table: 200 a month; a Receipts add-on gets the same.
      aiReadsMonthly: 200,
      invoicesMonthly: null,
      assistantTurnsMonthly: 300,
      vehicles: 5,
      // The forwarding inbox is Pro only (D-BOOK-2, Owner 2026-10-30).
      features: ["logbook", "receipts", "invoicing", "receipt_inbox"],
    },
  },
  addons: {
    // Single-feature add-ons A$19.90 / month, A$199 / year: entered per the coordinator brief of 2026-11-02;
    // Owner confirmed 2026-11-02 (decisions.md D-ADDON-PRICE-OK). Nothing reads these amounts yet. They grant the Pro
    // quota for that feature (receipts: 200 AI reads a month). Stripe SKUs: D-LAUNCH-Q5, T281.
    receipts: { monthlyCents: 1990, yearlyCents: 19900, grants: "pro" },
    invoicing: { monthlyCents: 1990, yearlyCents: 19900, grants: "pro" },
    // Extra vehicle A$3 / month, monthly only (D-PRICE-KEEP; D-LAUNCH-Q5 has no yearly vehicle price).
    // maxQuantity 50: same cap as team seats (the Owner has not set a separate one).
    vehicle: { monthlyCents: 300, maxQuantity: 50 },
  },
  team: {
    // D-TEAMS-PLAN, Owner 2026-10-29: A$39 / month incl. 5 non-Owner seats, A$6 / extra seat / month,
    // A$390 / year, A$60 / extra seat / year. D-LAUNCH-Q11: sold at launch.
    includedSeats: 5,
    // Upper bound on total seats sold online; the Owner has not set one (same cap as add-ons).
    maxSeats: 50,
    baseMonthlyCents: 3900,
    baseYearlyCents: 39000,
    extraSeatMonthlyCents: 600,
    extraSeatYearlyCents: 6000,
  },
  // 30-day card-free Pro trial (PRD, Owner 2026-10-07); D-TEAMS-PLAN gives the Team plan the same 30 days.
  trial: { days: 30, plan: "pro" },
  // Server upload cap 30 = quota 20 + 10 (PRD R6.11, Owner 2026-10-28; D-TEAMS point 2 "masked sync cap 30").
  freeTier: { maskedTripsAfterQuota: 10 },
  // D-PRICE-INTRO: new customers who start paying on or before 2027-03-31 (AEDT, end of day) keep
  // the offer 6 months (Owner 2026-10-31, T644). Discount figures are deliberately not stored.
  introOffer: { plan: "pro", billing: "monthly", endsOn: "2027-03-31" },
  // Plans the web account page sells (Owner, T762/T810). Add-ons and Teams are separate.
  webCheckoutPlans: ["logbook", "pro"],
  stripe: {
    // Price ids come from wrangler vars; these are the keys (D-LAUNCH-Q5: 13 prices).
    priceKeys: [
      "PRO_MONTH",
      "PRO_YEAR",
      "LOGBOOK_MONTH",
      "LOGBOOK_YEAR",
      "VEHICLE_MONTH",
      // Team plan (T503): base item (5 seats included) + extra-seat item (seat 6 onward).
      "TEAM_MONTH",
      "TEAM_YEAR",
      "SEAT_MONTH",
      "SEAT_YEAR",
      // Per-feature SKUs (T281): one feature bought on its own, cancelled on its own.
      "RECEIPTS_MONTH",
      "RECEIPTS_YEAR",
      "INVOICING_MONTH",
      "INVOICING_YEAR",
    ],
  },
} as const satisfies ProductConfigInput

/**
 * Validated when this module loads. An invalid edit throws `ProductConfigError` listing every
 * problem, so a bad config stops the Worker/test run at start instead of selling wrong numbers.
 * The literal types of `RAW` are kept so consumers see `20`, not `number`.
 */
export const PRODUCT_CONFIG: typeof RAW = validateProductConfig(RAW)
